<script setup>
import wineaccessLogoRoundBlack from '#assets/images/wineaccess-logo-round-black.svg'
import { isAvailable } from '#common/utils/catalog'
import ga4 from '#common/utils/ga4'
import { baseURL } from '#common/utils/url'
import { pushEvent } from '#common/utils/user'
import { scrollToProductInCarousel } from '~/utils/product'

const route = useRoute()
const router = useRouter()
const metadata = useMetadata()
const productResults = useProductResults()
const { $auth, $store, $config, $moment } = useNuxtApp()

const showMobileAddToCart = ref(false)

const { data: productData, error: productError } = await useFetch(
  `/api/product/${route.params.slug}`,
)

if (productError.value) {
  throw createError({ statusCode: 404, message: 'Product not found' })
}
if (productData.value?.url !== route.path) {
  await router.push({ path: productData.value?.url })
}
const available = computed(() =>
  isAvailable(
    $auth.user,
    productData.value || {},
    null,
    $config.public.validSiteIds,
  ),
)
const { data: cartCount } = await useFetch(
  `/api/product/${productData.value?.product_id}/cart_count/`,
)
const product = computed(() => {
  if (available.value) {
    return {
      ...productData.value,
      ...cartCount.value,
    }
  }
  return productData.value || {}
})

// Set product in Vuex store
await $store.dispatch('setProductObject', productData.value)

const scrollToPreviousProduct = computed(() => {
  return () => {
    if (productResults.productIDInView !== product.value.product_id) {
      scrollToProductInCarousel($store, nextTick)
    }
  }
})

const addToCartDiv = ref(null)
const notifyMeDiv = ref(null)
let addToCartObserver = null
let notifyMeObserver = null

const handleAddToCartIntersect = (entries) => {
  const [entry] = entries
  showMobileAddToCart.value =
    !entry.isIntersecting && entry.boundingClientRect.top > 0

  if (entry.boundingClientRect.top > 0) {
    return
  }

  setTimeout(() => {
    const boundingRect = entry.target.getBoundingClientRect()
    showMobileAddToCart.value = !entry.isIntersecting && boundingRect.top > 0
  }, 500)
}

const handleNotifyMeIntersect = (entries) => {
  const [entry] = entries
  showMobileAddToCart.value = !entry.isIntersecting
}

const ogUrl = computed(() => {
  if (product.value.og_url) {
    return product.value.og_url
  } else {
    return baseURL() + product.value?.url
  }
})
const ogDescription = computed(() => {
  if (product.value.og_description) {
    return product.value.og_description
  } else {
    return product.value.story_headline
  }
})
const ogImage = computed(() => {
  if (product.value.bottle_image_optimized_url) {
    return product.value.bottle_image_optimized_url
  } else {
    return product.value.bottle_image_url
  }
})

onMounted(() => {
  pushEvent(
    {
      metric_name: 'product viewed',
      product: product.value.product_id,
      url_path: route.fullPath,
    },
    $store,
  )
  ga4.trackViewItem({ product: product.value })
  showMobileAddToCart.value = available.value

  addToCartObserver = new IntersectionObserver(handleAddToCartIntersect, {
    root: null,
    threshold: 0,
  })
  if (addToCartDiv.value) {
    addToCartObserver.observe(addToCartDiv.value)
  }

  notifyMeObserver = new IntersectionObserver(handleNotifyMeIntersect, {
    root: null,
    threshold: 0,
  })
  if (notifyMeDiv.value) {
    notifyMeObserver.observe(notifyMeDiv.value)
  }
})

onBeforeUnmount(() => {
  if (addToCartObserver && addToCartDiv.value) {
    addToCartObserver.unobserve(addToCartDiv.value)
  }
  addToCartObserver = null

  if (notifyMeObserver && notifyMeDiv.value) {
    notifyMeObserver.unobserve(notifyMeDiv.value)
  }
  notifyMeObserver = null
})

useHead({
  title: product.value.title,
  meta: [
    // hid is used as unique identifier. Do not use `vmid` for it as it will not work
    {
      hid: 'description',
      name: 'description',
      content: product.value.story_headline,
    },
    {
      hid: 'product-id',
      name: 'product-id',
      content: product.value.product_id,
    },
    {
      hid: 'product-uuid',
      name: 'product-uuid',
      content: product.value.uuid,
    },
    {
      hid: 'og:description',
      name: 'og:description',
      property: 'og:description',
      content: ogDescription,
    },
    {
      hid: 'og:site_name',
      name: 'og:site_name',
      property: 'og:site_name',
      content: 'WineAccess.com',
    },
    {
      hid: 'og:url',
      name: 'og:url',
      property: 'og:url',
      content: ogUrl,
    },
    {
      hid: 'og:type',
      name: 'og:type',
      property: 'og:type',
      content: 'article',
    },
    {
      hid: 'og:title',
      name: 'og:title',
      property: 'og:title',
      content: product.value.title,
    },
    {
      hid: 'og:image:type',
      name: 'og:image:type',
      property: 'og:image:type',
      content: 'image/jpeg',
    },
    {
      hid: 'og:image:width',
      name: 'og:image:width',
      property: 'og:image:width',
      content: '1024',
    },
    {
      hid: 'og:image:height',
      name: 'og:image:height',
      property: 'og:image:height',
      content: '1024',
    },
    {
      hid: 'og:image',
      name: 'og:image',
      property: 'og:image',
      content: ogImage,
    },
  ],
})

function jsonld() {
  const today = new Date()
  const quarter = Math.floor(today.getMonth() / 3)
  const startFullQuarter = new Date(today.getFullYear(), quarter * 3, 1)
  const endFullQuarter = new Date(
    startFullQuarter.getFullYear(),
    startFullQuarter.getMonth() + 3,
    0,
  )
  let availability
  let firstShipDate
  availability =
    product.value.default_record && product.value.default_record.available
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock'
  if (product.value.is_offer) {
    const maxShipDate = Math.max(
      $moment(product.value.first_ship_date_ca),
      $moment(product.value.first_ship_date_ny),
    )
    if (maxShipDate > Date.now()) {
      firstShipDate = $moment(maxShipDate).format('YYYY-MM-DD')
      availability = 'https://schema.org/PreOrder'
    }
  }
  const result = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Product',
        name: product.value.title,
        image: product.value.bottle_image_url,
        description: product.value.content_teaser,
        sku: product.value.uuid,
        brand: {
          '@type': 'Brand',
          name: product.value.producing_winery || 'Wine Access',
        },
        offers: {
          '@type': 'Offer',
          price: product.value.price_excl_tax || product.value.retail_price,
          priceCurrency: 'USD',
          priceValidUntil: endFullQuarter,
          url: `${metadata.settingsVars.SEO_HOST}${product.value?.url}`,
          availability,
          availabilityStarts: firstShipDate,
        },
      },
    ],
  }
  if (
    product.value.rating &&
    product.value.rating_count &&
    product.value.rating_count > 0
  ) {
    result['@graph'][0].aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: product.value.rating,
      ratingCount: product.value.rating_count,
    }
  }
  const makeVideoObject = (thumbnailUrl) => {
    if (!thumbnailUrl) {
      thumbnailUrl = wineaccessLogoRoundBlack
    }
    result['@graph'].push({
      '@type': 'VideoObject',
      name: product.value.title,
      description:
        product.value.content_teaser ||
        product.value.story_headline ||
        product.value.narrative_tag_line,
      uploadDate: product.value.vintage
        ? new Date(product.value.vintage, 1, 1).toISOString()
        : product.value.featured_datetime || product.value.date_created,
      thumbnailURL: [thumbnailUrl],
      embedUrl: product.value.wine_video_url,
    })
  }
  if (!product.value.wine_video_url) {
    return result
  }
  if (product.value.wine_video_url.includes('youtube')) {
    makeVideoObject(
      `https://i.ytimg.com/vi_webp/${product.value.wine_video_url
        .split('/')
        .pop()}/sddefault.webp`,
    )
  } else if (product.value.wine_video_url.includes('vimeo')) {
    makeVideoObject(product.value.vimeo_thumbnail)
  }
  return result
}
useJsonld(jsonld())
</script>

<template>
  <main v-if="productData" class="wa-pdp">
    <template v-if="!available">
      <ProductOutOfStock :product="product" />
      <div ref="notifyMeDiv"></div>
    </template>
    <ProductDetails :product="product" />
    <ProductStory :product="product" />
    <div class="accent-rule-top">
      <ProductRecommendations
        :product="product"
        @recommendations-fetched="scrollToPreviousProduct"
      />
    </div>
    <StickyAddToCart
      v-if="available"
      :product="product"
      :show-mobile="showMobileAddToCart"
    />
    <template v-else>
      <ProductNotifyMeMobile
        v-if="showMobileAddToCart"
        :product="product"
        :floating="true"
      />
      <ProductNotifyMeMobile :product="product" />
    </template>
    <div ref="addToCartDiv"></div>
  </main>
</template>
